import React, { useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';

import type { RegistrationFormProps } from '@src/entities/registration';

import { sendAnalyticEvent } from '@app/web-analytic';

import type { AgreementsFormProps } from '@features/agreements';
import type { CreatePasswordFormProps } from '@features/create-password';

import {
  type CreateAgentRequestParams,
  useCreateAgent,
} from '@entities/agent-user';
import { useHandleRegistrationCodeErrors } from '@entities/registration';

import { FlkCode } from '@shared/constants';
import {
  AnalyticEvent,
  DateFormat,
  OTP,
  addTestAttribute,
  getConsents,
  phoneValueCasting,
  useStores,
} from '@shared/index';
import { FullWidthPrimaryButton } from '@shared/ui';

export const SubmitRegistration = observer(() => {
  const { t } = useTranslation();
  const {
    MainStore: {
      agentUserStore: { setAgent, setConfirmToken },
    },
  } = useStores();
  const {
    handleSubmit,
    setError,
    formState: { errors },
  } = useFormContext<
    RegistrationFormProps & CreatePasswordFormProps & AgreementsFormProps
  >();
  const navigate = useNavigate();
  const [requestParams, setRequestParams] = useState<CreateAgentRequestParams>({
    agent: {
      lastName: '',
      firstName: '',
      middleName: '',
      birthDate: '',
      phone: '',
      email: '',
      password: '',
    },
    consents: [],
  });

  const { res, refetch, error, isFetching } = useCreateAgent(
    {
      ...requestParams,
    },
    [requestParams]
  );

  useEffect(() => {
    if (!!requestParams.agent.lastName?.length) {
      refetch();
    }
  }, [requestParams]);

  useEffect(() => {
    if (!isFetching && res) {
      setAgent(res.agent);
      setConfirmToken(res.token);
      sendAnalyticEvent(AnalyticEvent.AGENT_REG_TO_STEP_OTP);
      navigate(OTP);
    }
  }, [isFetching, res]);

  const handleSpecificCodeErrors = useMemo(
    () => ({
      [FlkCode.AGT_0101_0002]: () => {
        // Пользователь с таким телефоном существует
        setError('phone', { type: 'value' });
        setError('root.phoneAlreadyExist', {
          message: t('COMMON:errors.phoneAlreadyExistAuth'),
        });
      },
      [FlkCode.RMM_6010_0005]: () => {
        // Пользователь с таким email существует
        setError('email', {
          message: t('COMMON:errors.emailAlreadyExist'),
        });
      },
      [FlkCode.RMM_6010_0014]: () => {
        // Неверный email
        setError('email', {
          message: t('COMMON:errors.incorrectEmail'),
        });
      },
    }),
    [setError]
  );

  useHandleRegistrationCodeErrors({
    error,
    setError,
    handleSpecificCodeErrors,
  });

  const submitPage = handleSubmit((values) => {
    const {
      lastName,
      firstName,
      middleName,
      birthDate,
      phone,
      email,
      personalDataAgreement,
      specialOffersAgreement,
      password,
    } = values;

    setRequestParams({
      agent: {
        lastName: lastName.trim(),
        firstName: firstName.trim(),
        middleName: middleName?.trim(),
        birthDate: format(new Date(birthDate), DateFormat.YYYY_MM_DD),
        phone: phoneValueCasting(phone),
        email,
        password,
      },
      consents: getConsents({
        personalDataAgreement,
        specialOffersAgreement,
      }),
    });
  });

  return (
    <FullWidthPrimaryButton
      size='l'
      onClick={submitPage}
      disabled={!!Object.keys(errors).length}
      isLoading={isFetching}
      {...addTestAttribute('registration.button.submit')}
    >
      {t('COMMON:buttons.continue')}
    </FullWidthPrimaryButton>
  );
});
